import React, { useEffect, useState } from 'react';

import { api } from '../../../../api/client';
import { useLanguage } from '../../../../i18n/LanguageProvider';
import { SectionHeading } from '../../../../ui';

interface PublicRequestStats {
  active_count: number;
  last_published_at: string | null;
}

function pluralize(value: number, one: string, few: string, many: string) {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

function relativePublishedLabel(value: string | null, now: number, language: 'ru' | 'en') {
  const timestamp = value ? new Date(value).getTime() : Number.NaN;
  if (!Number.isFinite(timestamp)) return language === 'en' ? 'latest request time unavailable' : 'время последней заявки неизвестно';

  const seconds = Math.max(0, Math.floor((now - timestamp) / 1000));
  if (seconds < 1) return language === 'en' ? 'latest request just now' : 'последняя только что';

  const units = seconds < 60
    ? { value: seconds, ru: ['секунду', 'секунды', 'секунд'], en: 'second' }
    : seconds < 3600
      ? { value: Math.floor(seconds / 60), ru: ['минуту', 'минуты', 'минут'], en: 'minute' }
      : seconds < 86400
        ? { value: Math.floor(seconds / 3600), ru: ['час', 'часа', 'часов'], en: 'hour' }
        : { value: Math.floor(seconds / 86400), ru: ['день', 'дня', 'дней'], en: 'day' };

  if (units.value < 30 || seconds < 86400) {
    if (language === 'en') {
      return `latest request ${units.value} ${units.en}${units.value === 1 ? '' : 's'} ago`;
    }
    return `последняя ${units.value} ${pluralize(units.value, units.ru[0], units.ru[1], units.ru[2])} назад`;
  }

  const publishedDate = new Date(timestamp);
  const includeYear = publishedDate.getFullYear() !== new Date(now).getFullYear();
  const date = new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    ...(includeYear ? { year: 'numeric' as const } : {}),
  }).format(publishedDate);
  return language === 'en' ? `latest request ${date}` : `последняя ${date}`;
}

export function LandingOrders() {
  const { language } = useLanguage();
  const [liveCount, setLiveCount] = useState<number | null>(null);
  const [lastPublishedAt, setLastPublishedAt] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let active = true;
    let generation = 0;
    const loadSummary = () => {
      const requestGeneration = ++generation;
      void api<PublicRequestStats>('/api/requests/public-stats/', { cache: 'no-store' })
        .then((stats) => {
          if (!active || requestGeneration !== generation) return;
          setLiveCount(stats.active_count);
          setLastPublishedAt(stats.last_published_at);
          setNow(Date.now());
        })
        .catch(() => undefined);
    };
    loadSummary();
    const refreshOnVisibility = () => {
      if (document.visibilityState === 'visible') loadSummary();
    };
    window.addEventListener('focus', loadSummary);
    document.addEventListener('visibilitychange', refreshOnVisibility);
    const refreshTimer = window.setInterval(loadSummary, 60_000);
    const clockTimer = window.setInterval(() => setNow(Date.now()), 1_000);

    return () => {
      active = false;
      window.removeEventListener('focus', loadSummary);
      document.removeEventListener('visibilitychange', refreshOnVisibility);
      window.clearInterval(refreshTimer);
      window.clearInterval(clockTimer);
    };
  }, []);

  return (
    <section className="section section--orders content-width" id="orders" data-figma-node="125:48197" aria-labelledby="orders-title">
      <SectionHeading
        spread
        title={<>Кого везут на LOVO<br />прямо сейчас</>}
        titleId="orders-title"
        description={
          <span className="live-line">
            <span className="live-badge"><i></i> live</span>
            <span className="live-count">Активных заявок:<b>{liveCount ?? '—'}</b></span>
            <i></i>
            <span className="live-last" data-i18n-skip>
              {liveCount === null || (liveCount === 0 && !lastPublishedAt) ? '' : relativePublishedLabel(lastPublishedAt, now, language)}
            </span>
          </span>
        }
        action={
          <a className="button button--medium" href="/portal/#carrier-search">
            Все заявки <img className="button__icon" src="/assets/figma/button-arrow-up-right-white.svg" alt="" />
          </a>
        }
      />
      <div className="orders-grid" id="orders-grid"></div>
      <a className="button button--medium orders-all-mobile" href="/portal/#carrier-search">
        Все заявки <img className="button__icon" src="/assets/figma/button-arrow-up-right-white.svg" alt="" />
      </a>
    </section>
  );
}
